'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type {
  FindingAssetItem,
  InternalEnrichedFinding,
} from '@terra/ui-design-system';
import {
  AssetTypeSchema,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import { useMainContentTabs } from '../../../store/layoutStore';
import { ENTITY_TAB_TYPE } from '../../../store/layoutStoreTypes';
import { useFindingListNavigationStore } from '../../../store/findingListNavigationStore';
import { GlobalMenuItemsTypeEnum } from '../../../store/globalContextMenuStore';
import { useGlobalContextMenu } from '../../../hooks/useGlobalContextMenu';
import { fetchFindingsNavigationIdsForTarget } from '../../../hooks/api/target-findings-api';
import type { FindingsListPagesOptions } from '../../../hooks/api/useFindingsListPages';
import { getSignalExternalNetworkAssetTab } from '../../../utils/signalSurfaceAssetNavigation';
import { createFindingObjectContextMention } from '../../../utils/objectContextMentionUtils';

const getUrlPathLabel = (url: string): string => {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
};

const getFindingTab = ({
  finding,
  fallbackTitle,
}: {
  finding: InternalEnrichedFinding;
  fallbackTitle: string;
}) => ({
  id: `${ENTITY_TAB_TYPE.FINDING}-${finding.id}`,
  title: finding.title || fallbackTitle,
  type: ENTITY_TAB_TYPE.FINDING,
  data: { findingId: finding.id },
});

export function useFindingsTableNavigation({
  targetId,
  targetType,
  findings,
  listOptions,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  findings: readonly InternalEnrichedFinding[];
  listOptions: FindingsListPagesOptions;
}) {
  const tCopilotTabTitles = useTranslations('chat.copilot.tabTitles');
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const setFindingIds = useFindingListNavigationStore(
    (state) => state.setFindingIds,
  );
  const navigationRequestIdRef = useRef(0);

  const hydrateFindingListNavigation = useCallback(() => {
    setFindingIds(findings.map((finding) => finding.id));

    if (!targetId) {
      return;
    }

    const requestId = (navigationRequestIdRef.current += 1);

    void fetchFindingsNavigationIdsForTarget({
      targetId,
      targetType,
      options: listOptions,
    })
      .then((orderedIds) => {
        if (
          requestId === navigationRequestIdRef.current &&
          orderedIds.length > 0
        ) {
          setFindingIds(orderedIds);
        }
      })
      .catch((error: unknown) => {
        console.warn('Failed to load finding navigation order', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });
  }, [findings, listOptions, setFindingIds, targetId, targetType]);
  const openFinding = useCallback(
    (finding: InternalEnrichedFinding) => {
      hydrateFindingListNavigation();
      navigateInActiveTab(
        getFindingTab({
          finding,
          fallbackTitle: tCopilotTabTitles('findingDetails'),
        }),
      );
    },
    [hydrateFindingListNavigation, navigateInActiveTab, tCopilotTabTitles],
  );
  const openFindingContextMenu = useCallback(
    ({
      event,
      finding,
    }: {
      event: MouseEvent;
      finding: InternalEnrichedFinding;
    }) => {
      hydrateFindingListNavigation();
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: getFindingTab({
              finding,
              fallbackTitle: tCopilotTabTitles('findingDetails'),
            }),
          },
          {
            type: GlobalMenuItemsTypeEnum.SEND_TO_CHAT,
            mention: createFindingObjectContextMention(finding),
            linkedObject: {
              kind: 'Finding',
              id: finding.id,
              label: finding.title,
            },
          },
        ],
      });
    },
    [hydrateFindingListNavigation, openContextMenu, tCopilotTabTitles],
  );
  const openSignal = useCallback(
    ({ signalId, title }: { signalId: string; title: string | null }) =>
      navigateInActiveTab({
        id: `signal-${signalId}`,
        title: title || tCopilotTabTitles('signalDetails'),
        type: ENTITY_TAB_TYPE.SIGNAL,
        data: { signalId },
      }),
    [navigateInActiveTab, tCopilotTabTitles],
  );
  const openAsset = useCallback(
    (asset: FindingAssetItem) => {
      if (asset.type === AssetTypeSchema.enum.DISCOVERED_ASSET) {
        const surfaceAssetTab = getSignalExternalNetworkAssetTab({
          assetId: asset.id,
          assetType: asset.discoveredAssetType,
          targetType,
        });

        if (surfaceAssetTab) {
          navigateInActiveTab(surfaceAssetTab);
        }

        return;
      }

      if (asset.type === AssetTypeSchema.enum.ENDPOINT) {
        navigateInActiveTab({
          id: `endpoint-${asset.id}`,
          title: asset.url
            ? getUrlPathLabel(asset.url)
            : tCopilotTabTitles('endpoint'),
          type: ENTITY_TAB_TYPE.ENDPOINT,
          data: { endpointId: asset.id, endpointUrl: asset.url ?? undefined },
        });

        return;
      }

      if (asset.type === AssetTypeSchema.enum.WEBPAGE) {
        navigateInActiveTab({
          id: `webpage-${asset.id}`,
          title: asset.url
            ? getUrlPathLabel(asset.url)
            : tCopilotTabTitles('webPage'),
          type: ENTITY_TAB_TYPE.WEBPAGE,
          data: { id: asset.id },
        });
      }
    },
    [navigateInActiveTab, tCopilotTabTitles, targetType],
  );

  return { openFinding, openFindingContextMenu, openSignal, openAsset };
}
